import { useCallback, useEffect, useState } from "react";
import { api, mesajEroare } from "./api.js";
import { tr } from "./i18n.js";

/**
 * MT20.4 — cine ce poate, așezat de firmă.
 *
 * Ecranul e o matrice: funcțiile pe rânduri, drepturile pe coloane, un tabel
 * per modul. Nu e un ecran frumos, e unul care trebuie CITIT corect — de-aia
 * fiecare drept își spune înțelesul pe modulul lui („scriere" pe stoc nu e
 * același lucru cu „scriere" pe tichete), iar un rând atins de firmă se vede
 * ca atare, cu drum înapoi la valoarea livrată.
 */

const ETICHETE_ROL = {
  ADMIN_TENANT: "Administrator", MANAGER_TEHNIC: "Manager tehnic", DISPECER: "Dispecer",
  COORDONATOR: "Coordonator", TEHNICIAN: "Tehnician", MAGAZIONER: "Magazioner",
  FINANCIAR: "Financiar", AUDITOR: "Auditor",
  CLIENT_ADMIN: "Client — administrator",
  CLIENT_UTILIZATOR: "Client — utilizator",
  CLIENT_FINANCIAR: "Client — financiar",
};

const ETICHETE_DREPT = {
  citire: "citește", scriere: "lucrează",
  administrare: "administrează", stornare: "stornează",
};

const ROLURI_CLIENT = ["CLIENT_ADMIN", "CLIENT_UTILIZATOR", "CLIENT_FINANCIAR"];

export default function Drepturi({ eu }) {
  const [module, setModule] = useState([]);
  const [incarca, setIncarca] = useState(true);
  const [eroare, setEroare] = useState("");
  const [lucreaza, setLucreaza] = useState("");
  const [grupDeschis, setGrupDeschis] = useState(null);

  const poateSchimba = eu.rol === "ADMIN_TENANT";

  const reincarca = useCallback(async () => {
    setIncarca(true);
    try { setModule(await api.drepturi()); setEroare(""); }
    catch (e) { setEroare(mesajEroare(e)); }
    finally { setIncarca(false); }
  }, []);

  useEffect(() => { reincarca(); }, [reincarca]);

  async function comuta(modul, rol, drept, bifat) {
    if (!poateSchimba) return;
    const m = module.find((x) => x.cod === modul);
    const rand = m?.randuri.find((r) => r.rol === rol);
    if (!rand) return;

    // Se trimite lista COMPLETĂ, nu „adaugă/scoate": două ecrane deschise
    // deodată și-ar călca bifele altfel.
    const noi = bifat
      ? [...new Set([...rand.drepturi, drept])]
      : rand.drepturi.filter((d) => d !== drept);

    setLucreaza(`${modul}|${rol}`);
    // Bifa se mută pe loc, ca ecranul să nu pară înțepenit; dacă serverul
    // refuză, se reîncarcă adevărul din bază.
    setModule((prev) => prev.map((x) => x.cod !== modul ? x : {
      ...x,
      randuri: x.randuri.map((r) => r.rol !== rol ? r : { ...r, drepturi: noi, personalizat: true }),
    }));
    try { await api.aseazaDrepturi(rol, modul, noi); setEroare(""); }
    catch (e) { setEroare(mesajEroare(e)); await reincarca(); }
    finally { setLucreaza(""); }
  }

  async function laImplicit(modul) {
    setLucreaza(modul);
    try { setModule(await api.drepturiLaImplicit(modul)); setEroare(""); }
    catch (e) { setEroare(mesajEroare(e)); }
    finally { setLucreaza(""); }
  }

  if (incarca) return <div className="gol">{tr("Se încarcă drepturile…")}</div>;

  const grupuri = [...new Set(module.map((m) => m.grup))];

  return (
    <div className="continut">
      <h2>{tr("Drepturi")}</h2>
      <p className="subtitlu">
        {tr("Cine ce poate face, pe fiecare modul. Valorile de pornire sunt cele livrate cu aplicația; firma le așază după cum îi sunt împărțite responsabilitățile.")}
        {!poateSchimba && " Doar administratorul firmei le poate schimba — tu le vezi."}
      </p>

      <div className="panou panou-atentie"><div className="panou-corp">
        <strong>{tr("Trei lucruri nu se pot bifa, oricâte drepturi ai.")}</strong>
        <div>
          {tr("Izolarea între firme și între clienți e impusă de baza de date, nu de ecranul ăsta — o bifă greșită poate strica o treabă, niciodată să scurgă date. Jurnalul nu se rescrie, iar o fișă semnată nu se modifică: îndreptarea unei greșeli se cheamă")} <strong>{tr("stornare")}</strong> {tr("și e chiar unul dintre drepturile de mai jos. Iar administratorul firmei are întotdeauna tot — altfel s-ar putea încuia singur pe dinafară.")}
        </div>
      </div></div>

      {eroare && <div className="eroare">{eroare}</div>}

      {grupuri.map((grup) => {
        const aleGrupului = module.filter((m) => m.grup === grup);
        const deschis = grupDeschis === null || grupDeschis === grup;
        return (
          <div key={grup} className="bloc-grup">
            <h2>
              {tr(grup)}{" "}
              <button className="buton buton-mic buton-sters"
                onClick={() => setGrupDeschis(deschis && grupDeschis !== null ? null : grup)}>
                {grupDeschis === grup ? tr("arată tot") : tr("doar grupul ăsta")}
              </button>
            </h2>
            {deschis && aleGrupului.map((m) => (
              <TabelModul
                key={m.cod} modul={m} poateSchimba={poateSchimba}
                lucreaza={lucreaza} comuta={comuta} laImplicit={laImplicit}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function TabelModul({ modul, poateSchimba, lucreaza, comuta, laImplicit }) {
  const atins = modul.randuri.some((r) => r.personalizat);
  const drepturi = modul.drepturiPosibile;

  return (
    <div className="card-modul">
      <div className="linie-actiune">
        <h3>
          {tr(modul.titlu)} <span className="cod-mic">{modul.cod}</span>
          {atins && <span className="pastila pastila-soft">{tr("așezat de firmă")}</span>}
        </h3>
        {poateSchimba && atins && (
          <button className="buton buton-mic buton-sters"
            disabled={lucreaza === modul.cod}
            onClick={() => laImplicit(modul.cod)}>
            {tr("revino la valorile livrate")}
          </button>
        )}
      </div>

      <div className="tabel-derulabil">
        <table className="tabel">
          <thead>
            <tr>
              <th>{tr("Funcția")}</th>
              {drepturi.map((d) => (
                <th key={d} className="num" title={tr(modul.explica[d])}>
                  {tr(ETICHETE_DREPT[d] ?? d)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {modul.randuri.map((r) => (
              <tr key={r.rol}>
                <td>
                  {tr(ETICHETE_ROL[r.rol] ?? r.rol)}
                  {r.rol === "ADMIN_TENANT" && (
                    <span className="fara-pret">{tr("are tot, prin definiție")}</span>
                  )}
                  {ROLURI_CLIENT.includes(r.rol) && (
                    <span className="fara-pret">{tr("cont al clientului deservit")}</span>
                  )}
                </td>
                {drepturi.map((d) => (
                  <td key={d} className="num">
                    <input
                      type="checkbox"
                      aria-label={tr("{0} — {1} pe {2}", tr(ETICHETE_ROL[r.rol] ?? r.rol), tr(ETICHETE_DREPT[d] ?? d), tr(modul.titlu))}
                      checked={r.rol === "ADMIN_TENANT" || r.drepturi.includes(d)}
                      disabled={!poateSchimba || r.rol === "ADMIN_TENANT"
                        || lucreaza === `${modul.cod}|${r.rol}`}
                      onChange={(e) => comuta(modul.cod, r.rol, d, e.target.checked)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <dl className="explica-drepturi">
        {drepturi.map((d) => (
          <div key={d}>
            <dt>{tr(ETICHETE_DREPT[d] ?? d)}</dt>
            <dd>{tr(modul.explica[d])}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
